<template>
    <div class="menu-button-wrapper">
        <Button
            id="settings-menu"
            type="button"
            icon="pi pi-bars"
            severity="secondary"
            @click="toggle"
            aria-haspopup="true"
            aria-controls="overlay_menu"
        />
    </div>
    <!-- This menu is launched from the NavRail footer (rail z-index: 1003) and
         its popup teleports to <body>. PrimeVue's default menu overlay base is
         1000, so the first open of the session computes to 1001 — below the
         rail — and renders underneath it; only once other overlays have bumped
         the shared counter past 1003 does it land on top (the intermittent
         "opens under the rail" bug). Pin it to the rail's own popup tier (1100,
         matching NavRail's switcher popup) so it always opens on top. -->
    <Menu
        ref="settingsMenu"
        :model="menuItems"
        :popup="true"
        :base-z-index="1100"
        :pt="{ root: { class: 'settings-menu' } }"
    >
        <template #item="{ item, props }">
            <a v-ripple class="flex items-center" v-bind="props.action">
                <span v-if="item.icon" :class="item.icon" />
                <img v-if="item.img" :src="item.img" class="profile-img" />
                <span>{{ item.label }}</span>
                <Badge v-if="item.badge" class="ml-auto" :value="item.badge" />
                <span
                    v-if="item.shortcut"
                    class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-xs p-1"
                >
                    {{ item.shortcut }}
                </span>
            </a>
        </template>
    </Menu>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { useKeybindingsExplanation } from "@/composables/keybindings-explanation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { visibleSettingsTabs } from "@/lib/organization-settings-tabs";
import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { useSteksManager } from "@/stores/steks-store";
import { useUIStore } from "@/stores/ui-store";
import { useUserManager } from "@/stores/user-store";
import { useWhatsNewManager, useWhatsNewStore } from "@/stores/whats-new-store";

const ui = useUIStore();
const keybindings = useKeybindingsExplanation();
const { signOut } = useAuthenticationStore();
const { authStatus } = useAuthenticationManager();
const isSignedIn = computed(() => authStatus.value === AuthStatus.SIGNED_IN);
const { photoUrl, currentOrganizationPermissions, isPlatformAdmin } =
    useUserManager();

const { hasUnseen: hasUnseenWhatsNew, unseenCount: unseenWhatsNewCount } =
    useWhatsNewManager();

const whatsNewStore = useWhatsNewStore();
// Dialog requires Vue's inject() context; initialize during component setup().
whatsNewStore.initDialog();

const settingsMenu = ref();
const toggle = (event: Event) => {
    settingsMenu.value.toggle(event);
};
const router = useRouter();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();

const { organizations } = useOrganizationsManager();
const { steks } = useSteksManager();

// Derived from the same tab list OrganizationSettings.vue renders, so a new tab
// can never be reachable in the page yet hidden from the only menu entry that
// leads there.
const canAccessOrgSettings = computed(
    () => visibleSettingsTabs(currentOrganizationPermissions.value).length > 0
);

const canManageStekSettings = computed(() => {
    return (
        currentOrganizationPermissions.value?.can_manage_stek_settings ?? false
    );
});

const canCreateStek = computed(() => {
    return currentOrganizationPermissions.value?.can_create_stek ?? false;
});

const hasOrganization = computed(() => {
    // Checking for name since that's used for the menu/navigation.
    return Boolean(organization.value?.name);
});

const hasMultipleOrganizations = computed(() => {
    return Boolean(organizations.value && organizations.value.length > 1);
});

const hasStek = computed(() => {
    // Checking for name since that's used for the menu/navigation.
    return Boolean(stek.value?.name);
});

const hasMultipleSteks = computed(() => {
    return Boolean(steks.value && steks.value.length > 1);
});

const menuItems = computed(() => [
    {
        label: "Settings",
        icon: photoUrl.value ? "" : "pi pi-user",
        img: photoUrl.value,
        class: "setting profile",
        command: () => router.push({ name: "settings" }),
        visible: isSignedIn.value,
    },
    {
        label: ui.darkMode ? "Light mode" : "Dark Mode",
        icon: ui.darkMode ? "pi pi-sun" : "pi pi-moon",
        command: () => ui.toggleDarkMode(),
    },
    {
        separator: true,
        visible: canAccessOrgSettings.value,
    },
    {
        label: "Organization Settings",
        icon: "pi pi-building",
        command: () =>
            router.push({
                name: "organizationSettings",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: canAccessOrgSettings.value,
    },
    {
        label: organization.value
            ? "Switch Organization"
            : "Select Organization",
        icon: "pi pi-sync",
        command: () => router.push({ name: "organizationPage" }),
        visible: hasMultipleOrganizations.value,
    },
    {
        separator: true,
        visible:
            (hasStek.value && canManageStekSettings.value) ||
            (hasOrganization.value && canCreateStek.value) ||
            (hasOrganization.value && hasMultipleSteks.value),
    },
    {
        label: "Stek Settings",
        icon: "pi pi-cog",
        command: () => router.push({ name: "stekSettings" }),
        visible: hasStek.value && canManageStekSettings.value,
    },
    {
        label: "Create new stek",
        icon: "pi pi-plus-circle",
        command: () =>
            router.push({
                name: "createStek",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: hasOrganization.value && canCreateStek.value,
    },
    {
        label: stek.value ? "Switch Stek" : "Select Stek",
        icon: "pi pi-sync",
        command: () =>
            router.push({
                name: "organizationHome",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: hasOrganization.value && hasMultipleSteks.value,
    },
    { separator: true },
    {
        label: "Docs",
        icon: "pi pi-book",
        command: () => router.push({ name: "docsHome" }),
    },
    {
        label: "What's New",
        icon: "pi pi-gift",
        badge: hasUnseenWhatsNew.value ? unseenWhatsNewCount.value : undefined,
        command: () => whatsNewStore.openModal(true),
    },
    {
        separator: true,
    },
    {
        label: "Help",
        icon: "pi pi-info-circle",
        command: () => router.push({ name: "help" }),
        class: "setting help",
    },
    {
        label: "Keybindings",
        icon: "pi pi-question-circle",
        command: () => keybindings.open(),
    },
    { separator: true },
    {
        label: "Logout",
        icon: "pi pi-sign-out",
        command: () => signOut(),
        visible: isSignedIn.value,
    },
    {
        separator: true,
        visible: isPlatformAdmin.value,
    },
    {
        label: "Platform Admin",
        icon: "pi pi-shield",
        command: () => router.push({ name: "platformAdmin" }),
        // Platform-wide flag — org-scoped permissions deliberately don't
        // surface this entry (backend admin endpoints 403 without it).
        visible: isPlatformAdmin.value,
    },
    {
        label: "Activity Metrics",
        icon: "pi pi-chart-line",
        command: () =>
            router.push({
                name: "activityMetrics",
            }),
        // Platform-wide operational insight — the platform flag, not an org role.
        visible: isPlatformAdmin.value,
    },
]);
</script>

<style lang="scss">
.profile-img {
    width: var(--p-icon-size);
    height: var(--p-icon-size);
    border-radius: 50%;
    object-fit: cover;
}

.menu-button-wrapper {
    position: relative;
    display: inline-flex;
}

// The popup is teleported to body, outside the themed application layout. Keep
// its surface tied to the app theme explicitly so it does not retain PrimeVue's
// light fallback tokens when dark mode is enabled.
html.dark-mode .settings-menu {
    background: var(--surface-background-mild);
    color: var(--text-color);

    .p-menu-item-content:hover {
        background: color-mix(
            in srgb,
            var(--surface-background) 70%,
            white 30%
        );
    }
}
</style>
